export type SalesWindow = 'not_open' | 'open' | 'closed' | 'unset';

export interface TicketType {
  key: string;
  name: string;
  priceCents: number | null;
  capacity: number | null;
  format: 'in_person' | 'virtual' | null;
  audienceKey: string | null;
  opensOn: string | null;
  closesOn: string | null;
  sold: number;
  comps: number;
  left: number | null;
  full: boolean;
  window: SalesWindow;
}

export interface Tickets {
  salesOpen: string | null;
  salesClose: string | null;
  eventbriteUrl: string | null;
  window: SalesWindow;
  types: TicketType[];
  unmatched: { name: string; sold: number }[];
  datesSet: boolean;
  soldFrom: 'sync' | 'upload';
  totals: { capacity: number | null; sold: number; comps: number };
}

export interface OutreachList {
  id: string;
  name: string;
  kinds: string[];
  tags: string[];
  audienceKey: string | null;
  count: number;
  withEmail?: number;
  contactIds?: string[];
  excludeIds?: string[];
  notes?: string | null;
}

export interface SendEntry {
  id: string;
  sentOn: string;
  kind: string;
  audienceKey: string | null;
  listId: string | null;
  onBehalfOf: { name: string | null; email: string | null } | null;
  channel: 'platform' | 'own_email';
  recipients: number;
  subject: string | null;
  note: string | null;
  automatic?: boolean;
}

export interface Comp {
  id: string;
  kind: 'comp_ticket' | 'fee_waiver';
  reason: string;
  inviteeId: string | null;
  group: string | null;
  person: string | null;
  quantity: number;
  ticketTypeKey: string | null;
  valueCents: number | null;
  approvedBy: string | null;
  code: string | null;
  note: string | null;
  cancelledAt: string | null;
}

export interface Partner {
  id: string;
  name: string;
  sharedMeals: { date: string; meal: string }[];
  headcount: Record<string, number>;
  thinkTankSeats: number | null;
  sharedSessions: number;
  contactId?: string | null;
  sharedSessionIds?: string[];
  sessions?: { id: string; title: string; day: string; startTime: string | null }[];
  delegates?: {
    eventName: string | null;
    groupCode: string | null;
    perDelegateCents: number | null;
    seatsReserved: number | null;
    registered: number | null;
    invoicedCents: number | null;
    creditNoteCents: number | null;
    vendorId: string | null;
  };
  vendor?: { id: string; name: string } | null;
  notes?: string | null;
  owedCents?: number | null;
  creditDueCents?: number | null;
  unusedSeats?: number | null;
}

export interface Registrant {
  inviteeId: string;
  name: string;
  email: string | null;
  status: string;
  source: string;
  stuckAt: 'registration' | 'ticket' | 'pheedloop' | null;
  done: { registration: boolean; ticket: boolean; pheedloop: boolean };
  pheedloop: string | null;
  tickets: number;
}

export interface Registration {
  visible: boolean;
  counts: {
    lists: number;
    sends: number;
    comps: {
      tickets: number;
      waivers: number;
      byReason: { key: string; label: string; count: number }[];
      valueCents: number;
    };
    partners: number;
    partnerGuests: number;
    registrants: number;
    registrantsComplete: number;
  };
  audiences: { key: string; name: string; public: boolean; days: string[] | null }[];
  tickets: Tickets;
  lists: OutreachList[];
  sendsByAudience: { key: string; name: string; sends: number; recipients: number; lastSentOn: string | null }[];
  progress: {
    steps: { key: string; label: string; done: number; stuckHere: number }[];
    complete: number;
    total: number;
    people?: Registrant[];
  };
  partners: Partner[];
  labels: {
    sendKinds: Record<string, string>;
    compKinds: Record<string, string>;
    compReasons: Record<string, string>;
    outreachKinds: string[];
    steps: { key: string; label: string }[];
  };
  sendLog?: SendEntry[];
  comps?: Comp[];
  sessions?: { id: string; title: string; day: string; startTime: string | null }[];
  vendors?: { id: string; name: string; vendorType: string }[];
}

export interface PreparedOutreach {
  listId: string;
  bcc: string[];
  recipients: number;
  unsubscribed: number;
  withoutEmail: number;
  subject: string;
  text: string;
}

export const WINDOW_LABEL: Record<SalesWindow, string> = {
  not_open: 'not open yet',
  open: 'on sale',
  closed: 'closed',
  unset: 'no dates',
};

export const KIND_LABEL: Record<string, string> = {
  department_head: 'Department heads',
  training_director: 'Training directors',
  partner: 'Partner organizations',
  supervisor: 'Supervisors',
  advertising_network: 'Advertising networks',
  outreach_target: 'Outreach targets',
  ambassador: 'Ambassadors',
};

export const MEAL_LABEL: Record<string, string> = {
  breakfast: 'Breakfast',
  am_break: 'Morning break',
  lunch: 'Lunch',
  pm_break: 'Afternoon break',
  dinner: 'Dinner',
};

export const dayText = (d: string | null) =>
  d
    ? new Date(`${d}T12:00:00Z`).toLocaleDateString('en-CA', {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

export function downloadText(fileName: string, contentType: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: contentType }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
